import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import useSheetViewport, { isIOS } from "../../../components/sheet/useSheetViewport";

/**
 * Contenedor de detalle responsivo: panel lateral (derecha) en escritorio y bottom
 * sheet en móvil. Mismo camino de entrada y salida (consistencia espacial); en móvil se
 * puede arrastrar para cerrar desde el header, con cierre por velocidad o distancia y
 * resistencia (rubber-band) si se arrastra hacia arriba.
 *
 * Motion: transiciones CSS sobre transform/opacity (interrumpibles), ease-out fuerte,
 * < 300 ms. Con prefers-reduced-motion solo hace crossfade (ver CSS).
 */
export default function Sheet({ open, onClose, side = "right", title, subtitle, header, tabs, footer, children }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const drag = useRef(null); // { startY, lastY, lastT, dy }
  const [dragging, setDragging] = useState(false);

  // Montar → siguiente frame → mostrar (para que la transición de entrada corra).
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 260); // debe cubrir la duración de salida
    return () => clearTimeout(t);
  }, [open]);

  // Escape cierra.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, onClose]);

  // Bloqueo de scroll, sheet pegado al área visible con teclado y campo a la vista
  // (compartido con el sheet del panel del negocio).
  useSheetViewport({ mounted, rootRef, panelRef, bodySelector: ".sa-sheet-body" });

  // Autofocus del primer campo, en escritorio y Android: se espera a que termine la
  // animación de entrada (260ms) para no pelear con ella. En iOS no (ver el Sheet del
  // panel): Safari deja el campo enfocado sin teclado y luego desplaza la página al tocarlo.
  useEffect(() => {
    if (!shown || isIOS) return;
    const panel = panelRef.current;
    if (!panel) return;
    const t = setTimeout(() => {
      panel.querySelector('.sa-sheet-body input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([disabled]), .sa-sheet-body textarea, .sa-sheet-body select')
        ?.focus({ preventScroll: true });
    }, 260);
    return () => clearTimeout(t);
  }, [shown]);

  // ---- Drag-to-dismiss (solo bottom sheet, solo desde el header) ----
  const onPointerDown = useCallback((e) => {
    if (side !== "bottom" || (e.pointerType === "mouse" && e.button !== 0)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startY: e.clientY, lastY: e.clientY, lastT: performance.now(), dy: 0, v: 0 };
    setDragging(true);
  }, [side]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !panelRef.current) return;
    const now = performance.now();
    const raw = e.clientY - d.startY;
    // Rubber-band hacia arriba: cuanto más jala, menos sigue.
    const dy = raw >= 0 ? raw : -(Math.abs(raw) * 0.35) / (1 + Math.abs(raw) / 200);
    const dt = Math.max(1, now - d.lastT);
    d.v = (e.clientY - d.lastY) / dt; // px/ms, signo = dirección
    d.lastY = e.clientY; d.lastT = now; d.dy = raw;
    panelRef.current.style.transform = `translate3d(0, ${dy}px, 0)`;
  }, []);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d || !panelRef.current) return;
    const height = panelRef.current.offsetHeight || 1;
    // Un flick rápido cierra aunque el recorrido sea corto; si no, por distancia.
    const shouldClose = d.v > 0.11 || d.dy > height * 0.3;
    panelRef.current.style.transform = ""; // vuelve a manos del CSS (abierto o cerrado)
    if (shouldClose) onClose?.();
  }, [onClose]);

  if (!mounted) return null;

  return (
    <div ref={rootRef} className={`sa-sheet-root sa-sheet-${side}`} data-state={shown ? "open" : "closed"}>
      <div className="sa-scrim" onClick={onClose} />
      <section
        ref={panelRef}
        className={`sa-sheet ${dragging ? "is-dragging" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header
          className="sa-sheet-head"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {side === "bottom" && <span className="sa-sheet-handle" aria-hidden />}
          <div className="sa-sheet-titlebar">
            <div className="sa-sheet-titles">
              <h2 className="sa-sheet-title">{title}</h2>
              {subtitle && <div className="sa-sheet-subtitle">{subtitle}</div>}
            </div>
            <button type="button" className="sa-iconbtn" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          {header}
          {tabs}
        </header>
        <div className="sa-sheet-body">{children}</div>
        {footer && <footer className="sa-sheet-foot">{footer}</footer>}
      </section>
    </div>
  );
}
